import { useAuth } from '@app/engine';
import { Logger } from '@shared/common';
import { BillingClient, FrontendConstants, HttpError, Telemetry, TenantClient } from '@shared/frontend';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { IOnboardingStatusDTO, ITenantDTO, ITenantUserDTO, OnboardingStep } from '@shared/common';
import type { IAccessDecisionDTO } from '@shared/frontend';

/** File-local constants for DashboardProvider. */
const localConstants = {
  logContext: 'DashboardProvider',
  // Statuses that are expected runtime outcomes of an auth-gated dashboard read, not faults: visitor/expired-or-absent session (401 "Not authenticated" from AuthClient when the local Firebase token
  // is silently unavailable), forbidden (403), or an authenticated account with no tenant yet (404 routes to onboarding). Logging these at Error is noise - the gate handles them by routing to
  // onboarding/login - so every gated fetch below filters them out of Error logging while still dispatching its error state.
  expectedGatedStatuses: [FrontendConstants.HTTPStatusUnauthorized, FrontendConstants.HTTPStatusForbidden, FrontendConstants.HTTPStatusNotFound] as readonly number[],
  // IANA timezone segment separator: the leading continent segment ('America', 'Europe', ...) is the low-cardinality region reported as a GA4 user property.
  timezoneSegmentSeparator: '/'
} as const;

/** Dashboard action types. */
enum DashboardActionType {
  TenantLoading,
  TenantLoaded,
  TenantError,
  TenantReset,
  BusinessNameLoaded,
  BillingLoading,
  BillingLoaded,
  BillingError,
  OnboardingLoading,
  OnboardingLoaded,
  OnboardingError
}

/** Dashboard state shape. */
interface IDashboardState {
  tenantLoading: boolean;
  tenantUser: ITenantUserDTO | null;
  tenantError: unknown | null;
  // The tenant's business display name (ITenantDTO.name), shown in the topbar. Resolved best-effort after the tenant user; null until loaded (consumers fall back to the member's displayName).
  businessName: string | null;
  billingLoading: boolean;
  billingChecked: boolean;
  billingAccess: IAccessDecisionDTO | null;
  // Onboarding progress: the dashboard gate's primary, fail-closed driver. The dashboard opens only once onboardingChecked is true AND onboardingComplete is true with no onboardingError.
  onboardingLoading: boolean;
  onboardingChecked: boolean;
  onboardingStep: OnboardingStep | null;
  onboardingComplete: boolean;
  onboardingTenantID: string | null;
  onboardingError: unknown | null;
}

/** Dashboard context shape exposed to consumers. */
interface IDashboardContext extends IDashboardState {
  tenantID: string | null;
  // True once billing access has been resolved and the tenant has no subscription yet (status === null): the dashboard gate must route to billing setup, not the dashboard.
  needsBilling: boolean;
  // Monotonic counter bumped by `refreshLabels`. Consumers of the tenant's niche/custom labels (the nav, overview, leads/bookings pages via `useNicheLabels`) include it in their profile-fetch deps
  // so a business-profile save re-resolves the displayed nouns everywhere, not just on the editing screen (each `useDashboardData` call is an isolated fetch with no shared cache).
  labelsVersion: number;
  refreshTenant: () => void;
  refreshBilling: () => void;
  refreshOnboarding: () => void;
  // Bump `labelsVersion` after a business-profile save so every `useNicheLabels` consumer refetches the profile and re-renders the updated Leads/Bookings nouns.
  refreshLabels: () => void;
}

/** Dashboard action union. */
type DashboardAction =
  | { type: DashboardActionType.TenantLoading }
  | { type: DashboardActionType.TenantLoaded; tenantUser: ITenantUserDTO }
  | { type: DashboardActionType.TenantError; error: unknown }
  | { type: DashboardActionType.TenantReset }
  | { type: DashboardActionType.BusinessNameLoaded; name: string }
  | { type: DashboardActionType.BillingLoading }
  | { type: DashboardActionType.BillingLoaded; access: IAccessDecisionDTO }
  | { type: DashboardActionType.BillingError }
  | { type: DashboardActionType.OnboardingLoading }
  | { type: DashboardActionType.OnboardingLoaded; status: IOnboardingStatusDTO }
  | { type: DashboardActionType.OnboardingError; error: unknown };

/**
 * Reducer for dashboard state.
 *
 * @param state - Current state.
 * @param action - Action to apply.
 * @returns The next state.
 */
function dashboardReducer(state: IDashboardState, action: DashboardAction): IDashboardState {
  switch (action.type) {
    case DashboardActionType.TenantLoading: {
      return { ...state, tenantLoading: true, tenantError: null };
    }
    case DashboardActionType.TenantLoaded: {
      return { ...state, tenantLoading: false, tenantUser: action.tenantUser, tenantError: null };
    }
    case DashboardActionType.TenantError: {
      return { ...state, tenantLoading: false, tenantUser: null, businessName: null, tenantError: action.error };
    }
    case DashboardActionType.TenantReset: {
      // The authenticated identity changed (a login in another app on the same origin replaced the single shared Firebase session). Drop the previous user's tenant, billing AND onboarding so the gate
      // re-evaluates from scratch — loading, then the new user's dashboard, onboarding, or billing setup — instead of momentarily showing the prior tenant's data.
      return {
        ...state,
        tenantLoading: true,
        tenantUser: null,
        tenantError: null,
        businessName: null,
        billingLoading: false,
        billingChecked: false,
        billingAccess: null,
        onboardingLoading: false,
        onboardingChecked: false,
        onboardingStep: null,
        onboardingComplete: false,
        onboardingTenantID: null,
        onboardingError: null
      };
    }
    case DashboardActionType.BillingLoading: {
      return { ...state, billingLoading: true };
    }
    case DashboardActionType.BusinessNameLoaded: {
      return { ...state, businessName: action.name };
    }
    case DashboardActionType.BillingLoaded: {
      return { ...state, billingLoading: false, billingChecked: true, billingAccess: action.access };
    }
    case DashboardActionType.BillingError: {
      // Billing access could not be resolved (transient billing-service/network error). Mark the read as completed (billingChecked) but with no access, so the gate fails OPEN to the dashboard rather
      // than trapping a paying tenant behind the setup gate or holding on a perpetual loading state; the backend AccessGate still enforces per-feature access on every request.
      return { ...state, billingLoading: false, billingChecked: true, billingAccess: null };
    }
    case DashboardActionType.OnboardingLoading: {
      return { ...state, onboardingLoading: true, onboardingError: null };
    }
    case DashboardActionType.OnboardingLoaded: {
      return {
        ...state,
        onboardingLoading: false,
        onboardingChecked: true,
        onboardingStep: action.status.step,
        onboardingComplete: action.status.complete,
        onboardingTenantID: action.status.tenantID,
        onboardingError: null
      };
    }
    case DashboardActionType.OnboardingError: {
      // Fail-CLOSED: the progress read failed (network/server fault), so we mark it checked to clear the loading state but keep onboardingComplete false and record the error. The gate then shows the
      // wizard's try-again state — it must NEVER fall through to the dashboard while onboarding is unresolved.
      return { ...state, onboardingLoading: false, onboardingChecked: true, onboardingComplete: false, onboardingError: action.error };
    }
    default: {
      return state;
    }
  }
}

/** Initial dashboard state. */
const initialState: IDashboardState = {
  tenantLoading: false,
  tenantUser: null,
  tenantError: null,
  businessName: null,
  billingLoading: false,
  billingChecked: false,
  billingAccess: null,
  onboardingLoading: false,
  onboardingChecked: false,
  onboardingStep: null,
  onboardingComplete: false,
  onboardingTenantID: null,
  onboardingError: null
};

/** Dashboard context instance. */
const DashboardContext = createContext<IDashboardContext | undefined>(undefined);

/**
 * Provider that resolves the authenticated tenant user. Language resolution lives in {@link LocaleProvider}.
 *
 * @param props - Component props.
 * @param props.children - Wrapped children.
 * @returns The provider element.
 */
export function DashboardProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  // Shared refresh signal for niche/custom label consumers (see IDashboardContext.labelsVersion).
  const [labelsVersion, setLabelsVersion] = useState(0);
  const refreshLabels = useCallback(() => setLabelsVersion((value) => value + 1), []);
  const tenantAbortRef = useRef<AbortController | null>(null);
  const billingAbortRef = useRef<AbortController | null>(null);
  const onboardingAbortRef = useRef<AbortController | null>(null);
  const lastAuthIDRef = useRef<string | null>(null);

  // When the authenticated identity changes — a login in another app on the same origin (e.g. the Portal) replaces the single shared Firebase session — discard the tenant resolved for the previous
  // user so the gate cannot keep showing their data. The resolution effect below then re-runs GetMe for the new user, landing on their dashboard or on onboarding when they have no tenant.
  useEffect(() => {
    const authID = user?.authID ?? null;
    const previousAuthID = lastAuthIDRef.current;
    lastAuthIDRef.current = authID;
    if (previousAuthID !== authID) {
      tenantAbortRef.current?.abort();
      billingAbortRef.current?.abort();
      onboardingAbortRef.current?.abort();
      dispatch({ type: DashboardActionType.TenantReset });
    }
  }, [user?.authID]);

  // Resolve the authenticated tenant user.
  const refreshTenant = useCallback(() => {
    if (!isAuthenticated || !user) return;
    tenantAbortRef.current?.abort();
    const controller = new AbortController();
    tenantAbortRef.current = controller;
    dispatch({ type: DashboardActionType.TenantLoading });
    TenantClient.GetMe(controller.signal)
      .then((tenantUser: ITenantUserDTO | null) => {
        if (controller.signal.aborted) return;
        if (!tenantUser) {
          // A null response means the authenticated account has no tenant yet; treat it the same as a 404 so the gate routes into onboarding.
          dispatch({ type: DashboardActionType.TenantError, error: new HttpError(FrontendConstants.HTTPStatusNotFound, 'Tenant user not found') });
          return;
        }
        dispatch({ type: DashboardActionType.TenantLoaded, tenantUser });
        Telemetry.Identify({ internalUserID: user.id, tenantID: tenantUser.tenantID, role: tenantUser.role, accountCreatedAt: tenantUser.createdAt });
        // Best-effort follow-up: resolve the tenant's BUSINESS name for the topbar (the member record only carries the person's displayName). Non-fatal — on failure consumers keep their fallback.
        TenantClient.GetMyTenant(controller.signal)
          .then((tenant: ITenantDTO) => {
            dispatch({ type: DashboardActionType.BusinessNameLoaded, name: tenant.name });
            // Enrich the GA4 user with a coarse region (the IANA timezone's continent segment) — low-cardinality by construction, never a precise location.
            const region = tenant.timezone?.split(localConstants.timezoneSegmentSeparator)[0];
            if (region) Telemetry.Identify({ internalUserID: user.id, region });
          })
          .catch((err: unknown) => {
            if (controller.signal.aborted) return;
            const status = err instanceof HttpError ? err.status : 0;
            if (!localConstants.expectedGatedStatuses.includes(status)) {
              Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
            }
          });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // A missing tenant (404) or an unauthenticated/forbidden session (401/403) is an expected state, not a fault: the gate routes these accounts into onboarding. Only genuine
        // failures (server/network errors) are logged.
        const status = err instanceof HttpError ? err.status : 0;
        if (!localConstants.expectedGatedStatuses.includes(status)) {
          Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        }
        dispatch({ type: DashboardActionType.TenantError, error: err });
      });
  }, [isAuthenticated, user]);

  useEffect(() => {
    refreshTenant();
    return () => tenantAbortRef.current?.abort();
  }, [refreshTenant]);

  // Resolve the authenticated account's onboarding progress. This is the dashboard gate's PRIMARY, fail-closed driver: the dashboard opens only once onboardingComplete is true. A failed read
  // (network/server fault) records onboardingError and keeps onboardingComplete false, so the gate shows the wizard's try-again state rather than ever exposing the dashboard. The wizard calls this
  // after each step (account/billing/phone) so the gate advances to the next step or, finally, opens the dashboard.
  const refreshOnboarding = useCallback(() => {
    if (!isAuthenticated || !user) return;
    onboardingAbortRef.current?.abort();
    const controller = new AbortController();
    onboardingAbortRef.current = controller;
    dispatch({ type: DashboardActionType.OnboardingLoading });
    TenantClient.GetOnboardingStatus(controller.signal)
      .then((status: IOnboardingStatusDTO) => {
        if (!controller.signal.aborted) dispatch({ type: DashboardActionType.OnboardingLoaded, status });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // A 401 (session gone/expired) or 403 is an expected auth-gated outcome, not a fault: the gate routes to login/onboarding and OnboardingError keeps the dashboard closed. Only genuine
        // server/network failures are logged. (A 404 cannot occur here - onboarding status exists for any authenticated account - but is harmless to include for consistency.)
        const status = err instanceof HttpError ? err.status : 0;
        if (!localConstants.expectedGatedStatuses.includes(status)) {
          Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        }
        dispatch({ type: DashboardActionType.OnboardingError, error: err });
      });
  }, [isAuthenticated, user]);

  useEffect(() => {
    refreshOnboarding();
    return () => onboardingAbortRef.current?.abort();
  }, [refreshOnboarding]);

  const tenantID = state.tenantUser?.tenantID ?? null;

  // Resolve the tenant's billing access once a tenant is known. A brand-new tenant has no subscription yet (status === null), which the onboarding wizard reads as "billing not verified yet".
  // Recomputed whenever the tenant changes; the billing step also calls this after a completed checkout so the wizard advances from billing to the phone step.
  const refreshBilling = useCallback(() => {
    if (!tenantID) return;
    billingAbortRef.current?.abort();
    const controller = new AbortController();
    billingAbortRef.current = controller;
    dispatch({ type: DashboardActionType.BillingLoading });
    BillingClient.GetAccess(controller.signal, tenantID)
      .then((access: IAccessDecisionDTO) => {
        if (controller.signal.aborted) return;
        dispatch({ type: DashboardActionType.BillingLoaded, access });
        // Best-effort GA4 enrichment: the plan tier user property comes from the gated subscription read (its decorated planName), which is not part of the access decision. Failures only cost the
        // user property, never the billing gate.
        if (!user) return;
        BillingClient.GetSubscription(controller.signal, tenantID)
          .then((subscription) => {
            if (subscription?.planName) Telemetry.Identify({ internalUserID: user.id, planTier: subscription.planName });
          })
          .catch((err: unknown) => {
            if (!controller.signal.aborted) Logger.Debug(localConstants.logContext, `Plan tier telemetry enrichment skipped: ${Logger.GetErrorMessage(err)}`);
          });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // A 401 (session gone/expired) or 403 is an expected auth-gated outcome, not a fault: the gate routes to login and BillingError keeps the dashboard closed. Only genuine server/network
        // failures are logged.
        const status = err instanceof HttpError ? err.status : 0;
        if (!localConstants.expectedGatedStatuses.includes(status)) {
          Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        }
        dispatch({ type: DashboardActionType.BillingError });
      });
  }, [tenantID, user]);

  useEffect(() => {
    if (!tenantID) return;
    refreshBilling();
    return () => billingAbortRef.current?.abort();
  }, [tenantID, refreshBilling]);

  const value = useMemo<IDashboardContext>(
    () => ({
      ...state,
      tenantID,
      needsBilling: state.billingChecked && state.billingAccess !== null && state.billingAccess.status === null,
      labelsVersion,
      refreshTenant,
      refreshBilling,
      refreshOnboarding,
      refreshLabels
    }),
    [state, tenantID, labelsVersion, refreshTenant, refreshBilling, refreshOnboarding, refreshLabels]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

/**
 * @returns The dashboard context.
 * @throws Error when used outside a DashboardProvider.
 */
export function useDashboard(): IDashboardContext {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within a DashboardProvider');
  return ctx;
}
